'use client';

import { Skeleton } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { useState } from 'react';

import {
  useGetChatDestinationsQuery,
  useOpenDirectMessageMutation,
} from '@/features/chat/api/chatApi';
import {
  getChatDestinationLabel,
  getChatUserDisplayName,
  isActiveDestination,
  toActiveChat,
} from '@/features/chat/utils/conversations';
import { useGetWorkspaceMembersQuery } from '@/features/workspaces/api/workspacesApi';
import { useAppSelector } from '@/store/hooks';
import { Avatar, ChevronIcon, CloseIcon, Divider, SearchBar } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './ChatPanel.module.scss';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { ChatConversation } from '@/features/chat/types';
import type { WorkspaceMemberItem } from '@/features/workspaces/types';

type DestinationItem = ChatConversation | ActiveChat;
const STREAM_DESTINATION: ActiveChat = {
  id: 'stream',
  type: 'stream',
  title: 'Stream',
  description: 'Activity across visible conversations',
};

const GENERAL_DESTINATION: ActiveChat = {
  id: 'general',
  type: 'general_channel',
  title: 'General',
  description: 'Workspace-wide coordination and operating notes',
  isReadOnly: true,
};

function getMemberDisplayName(member: WorkspaceMemberItem) {
  return getChatUserDisplayName(member.user);
}

function getMemberAvatar(member: WorkspaceMemberItem) {
  const user = member.user as WorkspaceMemberItem['user'] & {
    avatar_url?: string | null;
    avatar?: string | null;
  };

  return user.avatar_url ?? user.avatar ?? undefined;
}

function getDestinationUnreadCount(destination: ChatConversation) {
  return destination.unread_count ?? destination.message_count ?? 0;
}

function getItemLabel(
  destination: DestinationItem,
  currentUserEmail?: string | null,
) {
  return 'conversation_type' in destination
    ? getChatDestinationLabel(destination, currentUserEmail)
    : destination.title;
}

function getItemUnreadCount(destination: DestinationItem) {
  return 'conversation_type' in destination
    ? getDestinationUnreadCount(destination)
    : undefined;
}

function getItemActiveChat(
  destination: DestinationItem,
  currentUserEmail?: string | null,
): ActiveChat {
  return 'conversation_type' in destination
    ? toActiveChat(destination, currentUserEmail)
    : destination;
}

function getItemKey(destination: DestinationItem) {
  const type =
    'conversation_type' in destination
      ? destination.conversation_type
      : destination.type;

  return `${type}:${destination.id}`;
}

function DestinationButton({
  destination,
  activeChat,
  currentUserEmail,
  onSelect,
}: {
  destination: DestinationItem;
  activeChat: ActiveChat;
  currentUserEmail?: string | null;
  onSelect: (chat: ActiveChat) => void;
}) {
  const unreadCount = getItemUnreadCount(destination);
  const nextActiveChat = getItemActiveChat(destination, currentUserEmail);
  const isActive = isActiveDestination(activeChat, nextActiveChat);

  return (
    <button
      type="button"
      className={clsx(styles.chatPanel__destination, {
        [styles.chatPanel__destinationActive]: isActive,
      })}
      onClick={() => onSelect(nextActiveChat)}
    >
      <span>{getItemLabel(destination, currentUserEmail)}</span>
      {unreadCount && unreadCount > 0 ? (
        <span className={styles.chatPanel__destinationCount}>
          {unreadCount}
        </span>
      ) : null}
    </button>
  );
}

function DestinationGroup({
  label,
  count,
  showDivider = true,
  children,
}: {
  label?: string;
  count?: number;
  showDivider?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.chatPanel__destinationGroup}>
      {label ? (
        <div className={styles.chatPanel__destinationGroupHeader}>
          <span>{label}</span>
          {count && count > 0 ? <span>{count}</span> : null}
        </div>
      ) : null}
      {children}
      {showDivider ? <Divider className={styles.chatPanel__divider} /> : null}
    </section>
  );
}

function JobStatusGroup({
  label,
  destinations,
  defaultOpen = false,
  activeChat,
  currentUserEmail,
  onSelect,
}: {
  label: string;
  destinations: ChatConversation[];
  defaultOpen?: boolean;
  activeChat: ActiveChat;
  currentUserEmail?: string | null;
  onSelect: (chat: ActiveChat) => void;
}) {
  if (!destinations.length) {
    return null;
  }

  const count = destinations.reduce(
    (total, destination) => total + getDestinationUnreadCount(destination),
    0,
  );

  return (
    <details
      className={styles.chatPanel__destinationDisclosure}
      open={defaultOpen}
    >
      <summary>
        <span className={styles.chatPanel__destinationDisclosureLabel}>
          <span className={styles.chatPanel__destinationDisclosureIcon}>
            <ChevronIcon direction="right" size={10} />
          </span>
          {label}
        </span>
        {count > 0 ? <span>{count}</span> : null}
      </summary>
      {destinations.map((destination) => (
        <DestinationButton
          key={getItemKey(destination)}
          destination={destination}
          activeChat={activeChat}
          currentUserEmail={currentUserEmail}
          onSelect={onSelect}
        />
      ))}
    </details>
  );
}

function DirectMessageMemberSearch({
  currentUserEmail,
  onSelectChat,
}: {
  currentUserEmail?: string | null;
  onSelectChat: (chat: ActiveChat) => void;
}) {
  const [search, setSearch] = useState('');
  const query = search.trim();
  const { data, isFetching } = useGetWorkspaceMembersQuery(
    query
      ? {
          status: 'active',
          query,
          page: 1,
          page_size: 6,
        }
      : skipToken,
  );
  const [openDirectMessage, openDirectMessageState] =
    useOpenDirectMessageMutation();
  const members =
    data?.data?.results.filter(
      (member) => member.user.email !== currentUserEmail,
    ) ?? [];

  const openMemberDirectMessage = async (member: WorkspaceMemberItem) => {
    try {
      const response = await openDirectMessage({
        participant_ids: [member.user.id],
      }).unwrap();

      if (response.data) {
        onSelectChat(toActiveChat(response.data, currentUserEmail));
        setSearch('');
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <div className={styles.chatPanel__directMessageSearch}>
      <SearchBar
        placeholder="Search people..."
        value={search}
        onChange={setSearch}
        debounceMs={200}
        autoComplete="off"
      />
      {query ? (
        <div className={styles.chatPanel__directMessageResults}>
          {isFetching ? (
            <Skeleton variant="rounded" height={32} />
          ) : members.length ? (
            members.map((member) => {
              const label = getMemberDisplayName(member);

              return (
                <button
                  key={member.id}
                  type="button"
                  className={styles.chatPanel__directMessageMember}
                  disabled={openDirectMessageState.isLoading}
                  onClick={() => void openMemberDirectMessage(member)}
                >
                  <Avatar
                    size="small"
                    name={label}
                    src={getMemberAvatar(member)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <small>{member.user.email}</small>
                  </span>
                </button>
              );
            })
          ) : (
            <p className={styles.chatPanel__sidePanelEmpty}>No people found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function ChatDestinationsPanel({
  open,
  activeChat,
  onSelectChat,
  onClose,
}: {
  open: boolean;
  activeChat: ActiveChat;
  onSelectChat: (chat: ActiveChat) => void;
  onClose: () => void;
}) {
  const currentUserEmail = useAppSelector(
    (state) => state.auth.currentUser?.email,
  );
  const { data, isLoading } = useGetChatDestinationsQuery({
    page_size: 100,
  });
  const destinations = data?.data?.results ?? [];
  const attentionDestinations = destinations.filter((destination) =>
    ['unread', 'mentions'].includes(destination.conversation_type),
  );
  const generalDestination = destinations.find(
    (destination) => destination.conversation_type === 'general_channel',
  );
  const jobDestinations = destinations.filter(
    (destination) => destination.conversation_type === 'job_channel',
  );
  const directMessageDestinations = destinations.filter(
    (destination) => destination.conversation_type === 'direct_message',
  );
  const jobDestinationsByStatus = {
    active: jobDestinations.filter(
      (destination) => destination.job?.status === 'active',
    ),
    pending: jobDestinations.filter(
      (destination) => destination.job?.status === 'pending',
    ),
    completed: jobDestinations.filter(
      (destination) => destination.job?.status === 'completed',
    ),
    archived: jobDestinations.filter(
      (destination) => destination.job?.status === 'archived',
    ),
  };
  const ungroupedJobDestinations = jobDestinations.filter(
    (destination) =>
      !['active', 'pending', 'completed', 'archived'].includes(
        destination.job?.status ?? '',
      ),
  );
  const totalJobUnreadCount = jobDestinations.reduce(
    (total, destination) => total + getDestinationUnreadCount(destination),
    0,
  );
  const totalDirectMessageUnreadCount = directMessageDestinations.reduce(
    (total, destination) => total + getDestinationUnreadCount(destination),
    0,
  );

  return (
    <>
      {open ? (
        <button
          type="button"
          className={styles.chatPanel__sidePanelBackdrop}
          aria-label="Close channels"
          onClick={onClose}
        />
      ) : null}
      <aside
        className={clsx('card-container', styles.chatPanel__sidePanel, {
          [styles.chatPanel__sidePanelOpen]: open,
        })}
        aria-label="Chat channels"
      >
        <div className={styles.chatPanel__sidePanelHeader}>
          <strong>Channels</strong>
          <button type="button" onClick={onClose} aria-label="Close channels">
            <CloseIcon size={14} />
          </button>
        </div>

        {isLoading ? (
          <div className={styles.chatPanel__sidePanelBody}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={28} />
            ))}
          </div>
        ) : (
          <div className={styles.chatPanel__sidePanelBody}>
            <DestinationGroup>
              <DestinationButton
                destination={STREAM_DESTINATION}
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
              {attentionDestinations.map((destination) => (
                <DestinationButton
                  key={getItemKey(destination)}
                  destination={destination}
                  activeChat={activeChat}
                  currentUserEmail={currentUserEmail}
                  onSelect={onSelectChat}
                />
              ))}
            </DestinationGroup>

            <DestinationGroup>
              <DestinationButton
                destination={generalDestination ?? GENERAL_DESTINATION}
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
            </DestinationGroup>

            <DestinationGroup label="Job Channels" count={totalJobUnreadCount}>
              <JobStatusGroup
                label="Active"
                destinations={jobDestinationsByStatus.active}
                defaultOpen
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
              <JobStatusGroup
                label="Pending"
                destinations={jobDestinationsByStatus.pending}
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
              <JobStatusGroup
                label="Completed"
                destinations={jobDestinationsByStatus.completed}
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
              <JobStatusGroup
                label="Archived"
                destinations={jobDestinationsByStatus.archived}
                activeChat={activeChat}
                currentUserEmail={currentUserEmail}
                onSelect={onSelectChat}
              />
              {ungroupedJobDestinations.map((destination) => (
                <DestinationButton
                  key={getItemKey(destination)}
                  destination={destination}
                  activeChat={activeChat}
                  currentUserEmail={currentUserEmail}
                  onSelect={onSelectChat}
                />
              ))}
            </DestinationGroup>

            <DestinationGroup
              label="Direct Messages"
              count={totalDirectMessageUnreadCount}
              showDivider={false}
            >
              <DirectMessageMemberSearch
                currentUserEmail={currentUserEmail}
                onSelectChat={onSelectChat}
              />
              {directMessageDestinations.map((destination) => (
                <DestinationButton
                  key={getItemKey(destination)}
                  destination={destination}
                  activeChat={activeChat}
                  currentUserEmail={currentUserEmail}
                  onSelect={onSelectChat}
                />
              ))}
            </DestinationGroup>
          </div>
        )}
      </aside>
    </>
  );
}
